import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { projectsService } from '@/features/project/api/projects';
import type { FacadeAreaUnit } from '@/features/project/ui/FacadeAreaField';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { queryClient } from '@/shared/lib/query-client';
import { createLogger } from '@/shared/lib/logger';
import { ApiClientError } from '@/shared/lib/api';
import { userScopedStorage } from '@/shared/lib/user-scoped-storage';

const logger = createLogger('GoalsAutosaveCoordinator');

export type GoalsSaveStatus = 'idle' | 'saving' | 'error';

export interface GoalsSaveState {
  status: GoalsSaveStatus;
  error: string | null;
}

const DEFAULT_STATE: GoalsSaveState = { status: 'idle', error: null };

interface GoalsAutosaveStore {
  states: Record<string, GoalsSaveState>;
  setState: (projectId: string, state: GoalsSaveState) => void;
}

const useGoalsAutosaveStore = create<GoalsAutosaveStore>()(
  immer((set) => ({
    states: {},
    setState: (projectId, state) =>
      set((draft) => {
        draft.states[projectId] = state;
      }),
  }))
);

export interface GoalsPendingDraft {
  goals: string;
  region: string;
  /**
   * Facade area in the project's display unit. `undefined` = untouched (field
   * omitted from the PATCH). Sending it — even unchanged — marks the area
   * user-owned server-side and clears the "estimated" flag, so callers only
   * pass it when the user actually edited the field.
   */
  facadeArea?: number | null;
  /**
   * Display unit the `facadeArea` value is expressed in. Sent alongside it so
   * the server converts to canonical m² with the unit the user sees and
   * persists the preference. `undefined` = unit not touched (field omitted).
   */
  facadeAreaUnit?: FacadeAreaUnit;
}

// Per-project mutable plumbing — kept outside the Zustand store because they
// are non-serialisable (in-flight Promises) and must not drive re-renders.
// `pending` doubles as the local draft of a save that failed: it survives the
// editor unmounting, so the next editor mount re-seeds from it and the next
// leave / foreground retries it (the brief is never lost to a failed PATCH).
const pending = new Map<string, GoalsPendingDraft>();

// Failed-save drafts are ALSO mirrored to user-scoped localStorage so they
// survive a reload / closed tab (a failed PATCH on page hide would otherwise
// lose the brief). Scoped per user (wiped on logout by `wipeUserScopedKeys`)
// and per project. Written only on failure, removed on a successful save, so
// storage never holds a brief the server already has.
const draftStorage = userScopedStorage();
const draftKey = (projectId: string) => `goals-draft:${projectId}`;

function readStoredDraft(projectId: string): GoalsPendingDraft | null {
  const raw = draftStorage.getItem(draftKey(projectId));
  if (typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw) as GoalsPendingDraft;
    return typeof parsed?.goals === 'string' &&
      typeof parsed?.region === 'string'
      ? parsed
      : null;
  } catch {
    return null;
  }
}

/** In-memory draft first (newest), then one persisted by an earlier session. */
function readPending(projectId: string): GoalsPendingDraft | null {
  return pending.get(projectId) ?? readStoredDraft(projectId);
}
const inFlight = new Map<string, Promise<boolean>>();

function setSaveState(projectId: string, state: GoalsSaveState) {
  useGoalsAutosaveStore.getState().setState(projectId, state);
}

/**
 * Run the next pending save for `projectId`. Serialises per-project saves so
 * a leave-save and a render-trigger flush can't race the same PATCH.
 * Re-queues `pending` on failure so the user's edits aren't silently lost
 * — the next leave / foreground / flush retries.
 */
async function performSave(projectId: string): Promise<boolean> {
  const existing = inFlight.get(projectId);
  if (existing) {
    // Wait for the in-flight save to finish, then re-check pending — it may
    // have been swept up by that save or there may be newer keystrokes.
    await existing;
  }

  const target = readPending(projectId);
  if (!target) return true;
  pending.delete(projectId);

  setSaveState(projectId, { status: 'saving', error: null });

  // Server's localized reason for a failed PATCH; surfaced by callers that
  // must block on the save (render submit) instead of a generic error.
  let failureMessage: string | null = null;
  const promise: Promise<boolean> = (async () => {
    try {
      await projectsService.update(projectId, {
        goals: target.goals,
        construction_region: target.region,
        ...(target.facadeArea !== undefined
          ? { facade_area: target.facadeArea }
          : {}),
        ...(target.facadeAreaUnit !== undefined
          ? { facade_area_unit: target.facadeAreaUnit }
          : {}),
      });
      return true;
    } catch (err) {
      logger.warn('Goals PATCH failed', { projectId, error: err });
      failureMessage =
        err instanceof ApiClientError ? err.getFirstError() || null : null;
      return false;
    }
  })();

  inFlight.set(projectId, promise);
  let succeeded = false;
  try {
    succeeded = await promise;
  } finally {
    inFlight.delete(projectId);
  }

  if (succeeded) {
    // The server has this brief now; a newer failed draft would re-persist.
    void draftStorage.removeItem(draftKey(projectId));
    setSaveState(projectId, DEFAULT_STATE);
    // Refresh TanStack Query cache so the editor's `useProject` hook reflects
    // the new server-side baseline. Mirrors the `onSuccess` behaviour the old
    // `useMutation` had.
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projects.detail(projectId),
    });
    // The estimate-tab intro (empty state) shows region/units derived from
    // the goals' construction_region — a goals save must refresh it too.
    queryClient.invalidateQueries({
      queryKey: cacheKeys.estimates.introByProject(projectId),
    });
    return true;
  } else {
    // A newer edit may have been queued while this PATCH was in flight; it
    // supersedes the failed payload.
    if (!pending.has(projectId)) {
      pending.set(projectId, target);
      void draftStorage.setItem(draftKey(projectId), JSON.stringify(target));
    }
    setSaveState(projectId, {
      status: 'error',
      error: failureMessage,
    });
    return false;
  }
}

export const goalsAutosaveCoordinator = {
  /**
   * Buffer the latest edit and fire the PATCH immediately, fire-and-forget.
   * Called by the brief editor on leave (unmount, sheet close, tab hidden,
   * page hide) and on field blur — never per keystroke: every save runs the
   * server-side brief evaluation.
   */
  flushNow(
    projectId: string,
    goals: string,
    region: string,
    facadeArea?: number | null,
    facadeAreaUnit?: FacadeAreaUnit
  ) {
    pending.set(projectId, {
      goals: goals.trim(),
      region: region.trim(),
      facadeArea,
      facadeAreaUnit,
    });
    void performSave(projectId);
  },

  /**
   * Run any pending save now, and resolve once the
   * server confirms (or fails). Resolves `false` on save failure so callers
   * (e.g. render trigger) can surface the error and avoid launching a job
   * that would snapshot stale goals server-side.
   *
   * Resolves `true` when there is nothing pending — safe to call
   * unconditionally before every render submit.
   */
  async flushAndAwait(projectId: string): Promise<boolean> {
    return performSave(projectId);
  },

  /**
   * The unsaved draft left behind by a failed save, if any. The editor seeds
   * its buffer from it on mount so the user sees (and the next leave re-sends)
   * their text instead of the older server value.
   */
  getPending(projectId: string): GoalsPendingDraft | null {
    return readPending(projectId);
  },

  hasPending(projectId: string): boolean {
    return readPending(projectId) !== null;
  },

  /** Server message of the last failed save for this project, if any. */
  getSaveError(projectId: string): string | null {
    return useGoalsAutosaveStore.getState().states[projectId]?.error ?? null;
  },

  /**
   * Drop the pending payload and cached save state for a single project. Used
   * by `useDeleteProject`: without this a retried PATCH fires after the
   * project row is gone and the server returns 404, surfacing as a phantom
   * "Save failed" banner.
   *
   * Does NOT cancel an already-in-flight Promise (no AbortController in
   * `projectsService.update` yet); on completion the response is still
   * dropped because the project is no longer in the cache.
   */
  cancel(projectId: string) {
    pending.delete(projectId);
    void draftStorage.removeItem(draftKey(projectId));
    setSaveState(projectId, DEFAULT_STATE);
  },

  /**
   * Wipe every per-project queue. Called from `auth.store.logout()` so a
   * retried PATCH can't fire with a now-revoked token (or leak one account's
   * draft into the next account's session).
   */
  cancelAll() {
    pending.clear();
    // Drop every cached save state so the next account's project ids don't
    // hydrate with a stale "error" banner.
    useGoalsAutosaveStore.setState({ states: {} });
  },
};

/**
 * React hook returning the current save state for a project. Re-renders when
 * the coordinator changes status / error for this projectId.
 */
export function useGoalsSaveState(projectId: string): GoalsSaveState {
  return useGoalsAutosaveStore(
    (state) => state.states[projectId] ?? DEFAULT_STATE
  );
}
